/**
 * FERP Portal - Tabulator Primitive Manager
 *
 * Universal CRUD interface for ALL 25 FERP primitives using Tabulator tables.
 * Matches Error Trace Monitor quality: stat cards, polished table, export menu.
 *
 * DESIGN PRINCIPLES:
 * - DRY: ONE component for all 25 primitives
 * - Tabulator: Leverage 20+ built-in features (sorting, filtering, export, etc.)
 * - Data-Driven: Uses primitive-metadata.ts for UI config
 * - Dumb Frontend: All data from backend via GraphQL
 * - Shared Config: Uses TABULATOR_DEFAULTS + tabulator-mantine.css
 *
 * USAGE:
 * <TabulatorPrimitiveManager primitiveType="policy" />
 * <TabulatorPrimitiveManager primitiveType="workflow" />
 * ... works for all 25 primitives
 */

import { Tabulator } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Button,
  ThemeIcon,
  Paper,
  Badge,
  Loader,
  Center,
  Alert,
  Card,
  SimpleGrid,
  ActionIcon,
  Menu,
  Divider,
  Popover,
  ScrollArea,
  Tooltip,
  TextInput,
  Checkbox,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconRefresh,
  IconDownload,
  IconAlertTriangle,
  IconAlertCircle,
  IconPlugConnectedX,
  IconHelp,
  IconArrowRight,
  IconColumns,
  IconCheck,
  IconMaximize,
  IconMinimize,
  IconX,
  IconSearch,
  IconLayoutRows,
  IconRocket,
  IconFilter,
  IconTopologyFull,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTenantId } from '@/auth';
import {
  getPrimitiveMetadata,
  PHASE_CONFIG,
  LIFECYCLE_PHASES,
  getPrimitivesByPhase,
  type PrimitiveType,
} from '@/config/primitive-metadata';
// ✅ CORRECT: Repository Pattern (Layer 2)
import { usePrimitiveList, usePrimitiveMutations } from '@/hooks';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import type { PrimitiveMutationInput } from '@/hooks/usePrimitiveMutations';
import { TABULATOR_DEFAULTS, groupHeaderFormatter, createGroupHeaderFormatter, createCompoundGroupHeaderFormatter } from '@/utils/tabulatorConfig';
import { useResolveTaskGroupName } from '@/hooks/useResolveTaskGroupName';
import { useResolveRulesetName } from '@/hooks/useResolveRulesetName';
import { useScopeDimensions } from '@/hooks/useScopeDimensions';
import { EXPANDER_COLUMN, VISUAL_FORMATTER_MAP, STATUS_BADGE_MAP, type ExtendedColumn } from '@/utils/formFieldToTabulatorColumn';
import { actorNameFormatter, constellationNameFormatter } from '@/utils/tabulatorFormatters';
import { ACTOR_ID_FIELDS } from '@/contexts/ActorNameContext';
import { useActorNames } from '@/hooks/useActorNames';
import { useTableColumns } from '@/hooks/useTableColumns';
import { PrimitiveFormModal } from './PrimitiveFormModal';
import { PrimitiveDocumentationModal } from './PrimitiveDocumentationModal';
import { LensPresetSelector } from './LensPresetSelector';
import { InteractiveStatCard, type StatFilterScope } from '@/components/InteractiveStatCard';
import { useViewTracker } from '@/hooks/useViewTracker';
import { EntityDetailPanel } from '@/components/entity-detail';
import { useRowNavigation } from '@/components/entity-detail/useRowNavigation';
import { useEntityBreadcrumb } from '@/components/entity-detail/useEntityBreadcrumb';
import { useTerminology, useLensEvaluation } from '@ferp/workflow-forms';
import { useNavigate } from 'react-router-dom';
import { WorkflowLaunchModal } from './WorkflowLaunchModal';
import { useViewMode, type DisplayViewMode } from '@/hooks/useViewMode';
import { ViewModeToggle } from '@/components/shared/ViewModeToggle';
import { EntityCardGrid, primitiveFieldAdapter } from '@/components/shared/EntityCardGrid';
import { KanbanRenderer } from '@/components/renderers/KanbanRenderer';
import { SpatialRenderer, type SpatialCoordinate } from '@/components/renderers/SpatialRenderer';
import { TimelineRenderer } from '@/components/renderers/TimelineRenderer';
import { useSpatialLayout } from '@/hooks/useSpatialLayout';
import { useFocusContext } from '@/contexts/FocusContext';
import { MasterDetailLayout } from '@/components/shared/MasterDetailLayout';
import { EntityInlineDetail } from '@/components/shared/EntityInlineDetail';

// Tabulator imports
import '@/styles/tabulator-mantine.css';

// Tabulator types — use any for cross-version compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabulatorColumn = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabulatorCell = any;

// ============================================================================
// TYPES
// ============================================================================

/** A single column filter to apply on table build (mirrors Tabulator filter API). */
export interface TabulatorFilterDefinition {
  /** Column field name */
  field: string;
  /** Tabulator comparator — defaults to "=" */
  type?: '=' | '!=' | 'like' | 'starts' | '<' | '>' | '<=' | '>=';
  /** Value to match */
  value: string;
}

interface TabulatorPrimitiveManagerProps {
  primitiveType: PrimitiveType;
  /** Optional client-side filter applied to loaded items */
  clientFilter?: (item: PrimitiveItem) => boolean;
  /** Optional header content rendered above the table (e.g., filter toggles) */
  headerSlot?: React.ReactNode;
  /**
   * Selection mode: when provided, row click calls onSelect instead of opening
   * the detail drawer. Used by the Designer's WorkflowBrowser to pick a workflow.
   */
  onSelect?: (item: PrimitiveItem) => void;
  /** Hide the stat cards header (useful when embedded in a drawer) */
  compact?: boolean;
  /**
   * Override data: when provided, skip internal usePrimitiveList and use these items.
   * Enables parent to supply backend-filtered data (e.g., getTasksByActor).
   */
  overrideData?: { items: PrimitiveItem[] | null; loading: boolean; error?: unknown; refetch?: () => void };
  /**
   * Preset group-by field (controlled). When provided, the table groups rows by this
   * field on mount and whenever the prop changes. User can still override via the menu.
   * Pass `null` or omit to clear controlled grouping.
   *
   * Example: `groupBy="lifecyclePhase"` — groups task rows by lifecycle phase badge.
   */
  groupBy?: string | null;
  /**
   * Called when the user changes the group-by field via the toolbar menu.
   * Useful for parent components that want to persist or reflect the user's choice.
   */
  onGroupByChange?: (field: string | null) => void;
  /**
   * Column filters applied on table build. Each entry sets a header filter value
   * for the named column. Re-applied when the array reference changes (stable ref = no-op).
   *
   * Example: `filterBy={[{ field: 'status', value: 'ACTIVE' }]}`
   */
  filterBy?: TabulatorFilterDefinition[];
  /**
   * DRR-189 WS-2: Default view mode for this primitive. Used by USER-layer pages
   * to show card view instead of table for frontline staff. Only applies when the
   * user has no stored preference (first visit).
   */
  defaultViewMode?: import('@/hooks/useViewMode').DisplayViewMode;
  /** DRR-189: Render action slot per card in card view (e.g., Complete button) */
  renderCardAction?: (item: import('@/hooks/usePrimitiveData').PrimitiveItem) => React.ReactNode;
  /**
   * Context-specific label overrides for scope dimensions.
   * Maps dimension_id to custom label. Used when backend labels don't fit the page context.
   * Example: { subject_actor: 'By Staff Member', assigned_actor: 'By Trainee' }
   */
  dimensionLabelOverrides?: Record<string, string>;
}

// ============================================================================
// ERROR CLASSIFICATION
// ============================================================================

type ErrorType = 'network' | 'validation' | 'server' | 'graphql';

interface ApolloLikeError {
  networkError?: { result?: unknown; statusCode?: number };
  graphQLErrors?: Array<{ extensions?: { code?: string } }>;
}

function isApolloLikeError(error: unknown): error is ApolloLikeError {
  return typeof error === 'object' && error !== null;
}

function classifyError(error: unknown): ErrorType {
  if (!isApolloLikeError(error)) return 'graphql';
  const networkError = error.networkError;
  const graphQLErrors = error.graphQLErrors || [];

  // Check for GraphQL validation errors (query schema mismatch — NOT "backend unavailable")
  const hasValidationError = graphQLErrors.some(
    (e) => e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED'
  );
  if (hasValidationError) return 'validation';

  // True network error (can't reach server at all)
  if (networkError && !networkError.result && !networkError.statusCode) return 'network';

  // Server returned 5xx error
  if (networkError?.statusCode && networkError.statusCode >= 500) return 'server';

  return 'graphql';
}

const ERROR_DISPLAY: Record<ErrorType, { title: string; color: string; icon: typeof IconAlertTriangle }> = {
  network: { title: 'Backend Unavailable', color: 'orange', icon: IconPlugConnectedX },
  validation: { title: 'Query Configuration Error', color: 'yellow', icon: IconAlertCircle },
  server: { title: 'Server Error', color: 'red', icon: IconAlertTriangle },
  graphql: { title: 'Error Loading Data', color: 'red', icon: IconAlertTriangle },
};

const ERROR_MESSAGES: Record<ErrorType, string> = {
  network: 'Could not connect to the FERP Core backend. The service may be starting up or temporarily unavailable.',
  validation: 'The GraphQL query for this primitive has a schema mismatch. This is a frontend configuration issue, not a backend problem.',
  server: 'The backend returned a server error. Check gateway logs for details.',
  graphql: '', // Will use error.message
};

/** Actions column HTML variants */
const BASE_ACTION_BTNS = `<button class="ferp-tbl-action ferp-tbl-action--primary tabulator-btn-view" title="View / Edit">View/Edit</button><button class="ferp-tbl-action ferp-tbl-action--danger tabulator-btn-archive" title="Archive">Archive</button>`;
const WORKFLOW_ACTION_BTNS = `<button class="ferp-tbl-action ferp-tbl-action--run tabulator-btn-run" title="Run Workflow">▶ Run</button><button class="ferp-tbl-action tabulator-btn-instances" title="Running Instances" style="color:var(--mantine-color-violet-4)">Instances</button><button class="ferp-tbl-action ferp-tbl-action--primary tabulator-btn-view" title="View / Edit">View/Edit</button><button class="ferp-tbl-action ferp-tbl-action--danger tabulator-btn-archive" title="Archive">Archive</button>`;

/** Actions column — always last, always visible, never collapsed by responsiveLayout */
const ACTIONS_COLUMN: ExtendedColumn = {
  title: 'Actions',
  field: '_actions',
  width: 175,
  minWidth: 175,
  responsive: 0,
  hozAlign: 'center' as const,
  headerSort: false,
  essential: true,
  defaultVisible: true,
  formatter: () => BASE_ACTION_BTNS,
};


/** Filter columns based on visibility settings */
const getVisibleColumns = (
  allColumns: ExtendedColumn[],
  visibility: Record<string, boolean>,
): TabulatorColumn[] => {
  return allColumns
    .filter(col => {
      if (col.essential) return true;
      // Use explicit user setting if available, otherwise use defaultVisible
      if (visibility[col.field] !== undefined) return visibility[col.field];
      return col.defaultVisible !== false;
    })
    .map(({ essential, defaultVisible, category, ...col }) => {
      // Ensure every column has a safe formatter — Tabulator crashes when raw arrays/objects
      // are returned by the default formatter (e.g., tags: [], dependencies: [])
      if (!col.formatter) {
        col.formatter = (cell: TabulatorCell) => {
          const val = cell.getValue();
          if (val === null || val === undefined) return '';
          if (Array.isArray(val)) return val.join(', ');
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        };
      }
      return col;
    });
};

/** Bump this when FormSchema defaultVisible set changes — forces fresh defaults for all users */
const COLUMN_VISIBILITY_VERSION = 1; // Pre-production — reset on any column change. Bump to 2 at first release.

/** Load column visibility preferences from localStorage */
const loadColumnVisibility = (primitiveType: PrimitiveType): Record<string, boolean> => {
  try {
    const versionKey = `ferp-columns-v`;
    const storedVersion = parseInt(localStorage.getItem(versionKey) ?? '0', 10);
    if (storedVersion !== COLUMN_VISIBILITY_VERSION) {
      // Default set changed — clear all saved preferences so new defaults apply
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('ferp-columns-')) localStorage.removeItem(key);
      }
      localStorage.setItem(versionKey, String(COLUMN_VISIBILITY_VERSION));
      return {};
    }
    const stored = localStorage.getItem(`ferp-columns-${primitiveType}`);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/** Save column visibility preferences to localStorage */
const saveColumnVisibility = (primitiveType: PrimitiveType, visibility: Record<string, boolean>) => {
  try {
    localStorage.setItem(`ferp-columns-${primitiveType}`, JSON.stringify(visibility));
  } catch {
    // localStorage unavailable — fail silently
  }
};

// ============================================================================
// HELPERS
// ============================================================================

/** Convert camelCase field name to readable label */
// formatFieldName + FieldValue imported from @/components/RecordDetailDrawer (DRY)

// ============================================================================
// COMPONENT
// ============================================================================

export function TabulatorPrimitiveManager({ primitiveType, clientFilter, headerSlot, onSelect, compact, overrideData, groupBy: groupByProp, onGroupByChange, filterBy, defaultViewMode, renderCardAction, dimensionLabelOverrides }: TabulatorPrimitiveManagerProps) {
  const metadata = getPrimitiveMetadata(primitiveType);
  const phaseConfig = PHASE_CONFIG[metadata.phase];
  const PrimitiveIcon = metadata.icon;

  // Task group name resolver — translates "tg-4-3-001" → "Daily Care & Operations"
  const resolveTaskGroupName = useResolveTaskGroupName();

  // Industry-specific terminology: translate primitive name (e.g., "task" → "Patient Visit")
  const { translate } = useTerminology();
  const translated = translate(primitiveType);
  const label = translated === primitiveType ? metadata.name : translated;

  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const navigate = useNavigate();

  // ============================================================================
  // DATA LOADING ✅ CORRECT - Repository Pattern (Layer 2)
  // ============================================================================

  const internal = usePrimitiveList(primitiveType);
  const { items, loading, error, refetch } = overrideData
    ? { items: overrideData.items, loading: overrideData.loading, error: overrideData.error, refetch: overrideData.refetch ?? internal.refetch }
    : internal;
  const rawData: PrimitiveItem[] = clientFilter
    ? (items ?? []).filter(clientFilter)
    : (items ?? []);

  // DRR-178 Phase 3: Focus Context — filter data to focused subject/performer.
  // CRITICAL safety events always break through (DRR-178 D4).
  const { focusedSubject, perspective, isFocused } = useFocusContext();
  const data: PrimitiveItem[] = useMemo(() => {
    if (!isFocused || !focusedSubject) return rawData;
    return rawData.filter((item) => {
      // Critical breakthrough: always show CRITICAL items regardless of focus
      if ((item as Record<string, unknown>).safetyLevel === 'CRITICAL') return true;
      // Filter by focus perspective
      const field = perspective === 'performer' ? 'assignedActorId' : 'subjectActorId';
      return (item as Record<string, unknown>)[field] === focusedSubject;
    });
  }, [rawData, isFocused, focusedSubject, perspective]);

  const { create, archive: archiveEntity, loading: mutationLoading } = usePrimitiveMutations(primitiveType);

  // DRR-178: Backend-driven scope dimensions (replaces GROUPABLE_SYSTEM_FIELDS)
  const { dimensions: rawScopeDimensions, fallbackFields } = useScopeDimensions(primitiveType);

  // Apply context-specific label overrides (e.g., training page: "By Staff Member" instead of "By Resident")
  const scopeDimensions = useMemo(() => {
    if (!dimensionLabelOverrides || Object.keys(dimensionLabelOverrides).length === 0) {
      return rawScopeDimensions;
    }
    return rawScopeDimensions.map((dim) => ({
      ...dim,
      label: dimensionLabelOverrides[dim.dimensionId] ?? dim.label,
    }));
  }, [rawScopeDimensions, dimensionLabelOverrides]);

  // DRR-179: Spatial layout — fetches LOCATED_AT edges with coordinates + floor plan
  // Cheap no-op when no edges exist. Provides coordinates to SpatialRenderer.
  const { layout: spatialNodes, hasCoordinates: hasSpatialCoords, floors: spatialFloors } = useSpatialLayout();

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [docsOpened, { open: openDocs, close: closeDocs }] = useDisclosure(false);

  // Workflow launch state (used only when primitiveType === 'workflow')
  const [launchOpened, { open: openLaunch, close: closeLaunch }] = useDisclosure(false);
  const [launchItem, setLaunchItem] = useState<PrimitiveItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<PrimitiveItem | null>(null);
  const [drawerTab, setDrawerTab] = useState<string | null>('details');
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility(primitiveType)
  );

  const tableRef = useRef<HTMLDivElement>(null);
  const tabulatorInstance = useRef<any | null>(null);
  const tableBuiltRef = useRef(false);
  // Guard to prevent Effect 2 (replaceData) from running while Effect 1 is
  // between destroy and tableBuilt — eliminates "row cannot be found" errors.
  const rebuildingRef = useRef(false);
  // Generation counter — each new Tabulator instance gets a unique ID so that
  // a stale async `tableBuilt` callback from a destroyed instance cannot
  // accidentally set tableBuiltRef.current = true for the wrong instance.
  const tableGenRef = useRef(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // View mode: table or card (persisted in localStorage)
  // DRR-189: defaultViewMode from parent overrides the 'table' default for first visit.
  const { viewMode, setViewMode } = useViewMode(primitiveType, defaultViewMode);
  const { resolveActorName } = useActorNames();
  // DRR-117: Ruleset name resolution for mandate badges
  const resolveRulesetName = useResolveRulesetName();
  // DRR-288: Inject terminology resolution for domain-agnostic labels
  const cardFieldAdapter = useMemo(
    () => primitiveFieldAdapter(primitiveType, resolveActorName, translate, resolveRulesetName),
    [primitiveType, resolveActorName, translate, resolveRulesetName]
  );

  // View tracker for "new" / unviewed entities.
  // Destructure individual stable callbacks (useCallback inside useViewTracker) rather than
  // holding the return object — the object is a new reference every render, which destabilizes
  // handleView → triggers Effect 1 (table destroy/recreate) on every render → flash.
  const { markViewed, isViewed: isEntityViewed } = useViewTracker(primitiveType);
  const [activeStatFilter, setActiveStatFilter] = useState<{ category: string; scope: StatFilterScope } | null>(null);

  // Group-by support — can be controlled via groupByProp or driven by toolbar menu
  const [groupField, setGroupField] = useState<string | null>(groupByProp ?? null);
  // DRR-178 Phase 4: Compound scoping — secondary group-by for 2-level grouping
  const [secondaryGroupField, setSecondaryGroupField] = useState<string | null>(null);

  // Sync controlled groupByProp → internal state when it changes from the parent
  useEffect(() => {
    if (groupByProp !== undefined) {
      setGroupField(groupByProp ?? null);
    }
  }, [groupByProp]);

  // Row expansion (accordion) tracking
  const expandedRowsRef = useRef<Set<string>>(new Set());

  // Arrow-key row navigation when detail panel is open
  const rowNavigation = useRowNavigation({
    data,
    selectedEntity: selectedItem,
    panelOpened: drawerOpened,
    onNavigate: (entity) => {
      markViewed(entity.id as string);
      setSelectedItem(entity);
    },
    tabulatorRef: tabulatorInstance,
  });

  // Breadcrumb context — resolves parent chain for navigation
  const breadcrumbs = useEntityBreadcrumb({
    entity: selectedItem,
    primitiveType,
    onNavigate: (navType, entityId) => {
      // Navigate to parent entity if possible
      const target = data?.find(item => (item.id as string) === entityId);
      if (target) {
        markViewed(entityId);
        setSelectedItem(target);
      }
    },
  });

  // ============================================================================
  // SCHEMA-DRIVEN COLUMNS (Layer 1: FormSchema, Layer 2: Lens)
  // ============================================================================

  const {
    evaluation,
    activeLenses,
    isEvaluating,
    evaluateLens,
    fetchActiveLenses,
  } = useLensEvaluation();

  const [selectedLensId, setSelectedLensId] = useState<string | null>(null);

  const {
    lensFilteredColumns,
    groupableFields: schemaGroupableFields,
    loading: schemaLoading,
    hasSchema,
    sectionOrder,
  } = useTableColumns({
    primitiveType,
    lensVisibleFields: evaluation?.visibleFields,
  });

  // Fetch available lenses on mount only — fetchActiveLenses ref is unstable
  // (Apollo useLazyQuery returns new function refs on cache state changes)
  useEffect(() => {
    fetchActiveLenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // All available columns: schema-driven ONLY — no hardcoded fallback (DRR-055)
  // EXPANDER_COLUMN prepended for row expansion (table/master-detail views)
  const allColumns = useMemo(() => {
    // Workflow gets an extra "Run" button in its actions column
    const actionsCol: ExtendedColumn = primitiveType === 'workflow'
      ? { ...ACTIONS_COLUMN, width: 310, minWidth: 310, formatter: () => WORKFLOW_ACTION_BTNS, category: 'System' }
      : { ...ACTIONS_COLUMN, category: 'System' };

    // Schema-driven path ONLY — columns come from backend FormSchema
    const cols: ExtendedColumn[] = [...lensFilteredColumns, actionsCol];
    // Apply visual formatters to matching columns (sparkline, heatmap, avatar stack)
    for (const col of cols) {
      if (col.field && VISUAL_FORMATTER_MAP[col.field] && !col.formatter) {
        col.formatter = VISUAL_FORMATTER_MAP[col.field];
      }
    }
    // Apply actor name resolution (overrides existing formatters on actor ID fields)
    const actorFormatter = actorNameFormatter(resolveActorName);
    for (const col of cols) {
      if (col.field && ACTOR_ID_FIELDS.has(col.field)) {
        col.formatter = actorFormatter;
      }
    }
    // Resolve constellation IDs to resident names via row's subjectActorId
    const constellationFormatter = constellationNameFormatter(resolveActorName);
    for (const col of cols) {
      if (col.field === 'constellationId') {
        col.formatter = constellationFormatter;
      }
    }
    // Prepend expander column for row expansion
    return [EXPANDER_COLUMN, ...cols];
  }, [lensFilteredColumns, primitiveType, resolveActorName]);

  // Toggle a column's visibility
  const toggleColumn = useCallback((field: string) => {
    setColumnVisibility(prev => {
      const col = allColumns.find(c => c.field === field);
      const currentlyVisible = prev[field] !== undefined ? prev[field] : col?.defaultVisible !== false;
      const next = { ...prev, [field]: !currentlyVisible };
      saveColumnVisibility(primitiveType, next);
      return next;
    });
  }, [primitiveType, allColumns]);

  // Reset column visibility to defaults
  const resetColumns = useCallback(() => {
    setColumnVisibility({});
    saveColumnVisibility(primitiveType, {});
  }, [primitiveType]);

  // Is a column currently visible?
  const isColumnVisible = useCallback((field: string): boolean => {
    const col = allColumns.find(c => c.field === field);
    if (col?.essential) return true;
    if (columnVisibility[field] !== undefined) return columnVisibility[field];
    return col?.defaultVisible !== false;
  }, [allColumns, columnVisibility]);

  // ============================================================================
  // GROUPED COLUMN MENU (search + section groups + toggle-all)
  // ============================================================================

  const [columnSearch, setColumnSearch] = useState('');

  // Build ordered category groups for the column menu
  const columnGroups = useMemo(() => {
    const toggleable = allColumns.filter(
      col => !col.essential && col.field !== '_actions'
    );

    // Filter by search query
    const filtered = columnSearch.trim()
      ? toggleable.filter(col =>
          col.title.toLowerCase().includes(columnSearch.trim().toLowerCase())
        )
      : toggleable;

    // Group by category
    const groups = new Map<string, ExtendedColumn[]>();
    for (const col of filtered) {
      const cat = col.category ?? 'Other';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(col);
    }

    // Determine render order: "System" first, then sectionOrder, then "Other" last
    const orderedCategories: string[] = [];
    if (groups.has('System')) orderedCategories.push('System');
    for (const title of sectionOrder) {
      if (groups.has(title) && title !== 'System') {
        orderedCategories.push(title);
      }
    }
    // Any categories not in sectionOrder (edge case)
    for (const cat of groups.keys()) {
      if (!orderedCategories.includes(cat) && cat !== 'Other') {
        orderedCategories.push(cat);
      }
    }
    if (groups.has('Other')) orderedCategories.push('Other');

    return orderedCategories.map(cat => ({
      category: cat,
      columns: groups.get(cat) ?? [],
    }));
  }, [allColumns, sectionOrder, columnSearch]);

  // Toggle all columns in a group
  const toggleGroupVisibility = useCallback((columns: ExtendedColumn[]) => {
    setColumnVisibility(prev => {
      const next = { ...prev };
      // If ALL are visible, hide all. Otherwise show all.
      const allVisible = columns.every(col => {
        if (next[col.field] !== undefined) return next[col.field];
        return col.defaultVisible !== false;
      });
      for (const col of columns) {
        next[col.field] = !allVisible;
      }
      saveColumnVisibility(primitiveType, next);
      return next;
    });
  }, [primitiveType]);

  // Select all / deselect all non-essential columns
  const selectAllColumns = useCallback(() => {
    setColumnVisibility(prev => {
      const next = { ...prev };
      for (const col of allColumns) {
        if (!col.essential && col.field !== '_actions') {
          next[col.field] = true;
        }
      }
      saveColumnVisibility(primitiveType, next);
      return next;
    });
  }, [primitiveType, allColumns]);

  const deselectAllColumns = useCallback(() => {
    setColumnVisibility(prev => {
      const next = { ...prev };
      for (const col of allColumns) {
        if (!col.essential && col.field !== '_actions') {
          next[col.field] = false;
        }
      }
      saveColumnVisibility(primitiveType, next);
      return next;
    });
  }, [primitiveType, allColumns]);

  // ============================================================================
  // FULLSCREEN TOGGLE
  // ============================================================================

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => {
      const next = !prev;
      try {
        window.dispatchEvent(new Event(next ? 'layout:collapseSidebar' : 'layout:expandSidebar'));
      } catch { /* sidebar event not required */ }
      return next;
    });
  }, []);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
        try { window.dispatchEvent(new Event('layout:expandSidebar')); } catch { /* */ }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Resize Tabulator when entering/exiting fullscreen
  useEffect(() => {
    if (!tabulatorInstance.current || !tableBuiltRef.current || rebuildingRef.current) return;
    try {
      if (isFullscreen) {
        tabulatorInstance.current.setHeight('calc(100vh - 80px)');
      } else {
        tabulatorInstance.current.setHeight('600px');
      }
    } catch { /* tabulator may not be ready */ }
  }, [isFullscreen]);

  // ============================================================================
  // COMPUTED STATS (total + new/unviewed counts)
  // ============================================================================

  const activeCount = data.filter((item) => item.active !== false).length;
  const criticalCount = data.filter((item) => item.safetyLevel === 'CRITICAL').length;
  const newTotalCount = data.filter((item) => !isEntityViewed(item.id as string)).length;
  const newActiveCount = data.filter((item) => item.active !== false && !isEntityViewed(item.id as string)).length;
  const newCriticalCount = data.filter((item) => item.safetyLevel === 'CRITICAL' && !isEntityViewed(item.id as string)).length;

  // ============================================================================
  // STAT CARD FILTER APPLICATION (applies activeStatFilter to Tabulator)
  // ============================================================================

  useEffect(() => {
    if (!tabulatorInstance.current || !tableBuiltRef.current || rebuildingRef.current) return;

    if (!activeStatFilter) {
      try { tabulatorInstance.current.clearFilter(); } catch { /* table may be mid-rebuild */ }
      return;
    }

    try { tabulatorInstance.current.setFilter((rowData: PrimitiveItem) => {
      let matchesCategory = true;
      switch (activeStatFilter.category) {
        case 'active':
          matchesCategory = rowData.active !== false;
          break;
        case 'critical':
          matchesCategory = rowData.safetyLevel === 'CRITICAL';
          break;
        case 'total':
        default:
          matchesCategory = true;
          break;
      }

      if (activeStatFilter.scope === 'new') {
        return matchesCategory && !isEntityViewed(rowData.id as string);
      }

      return matchesCategory;
    }); } catch { /* table may be mid-rebuild */ }
  }, [activeStatFilter, isEntityViewed]);

  // Filtered data for card view (mirrors Tabulator's setFilter logic above)
  const filteredData = useMemo(() => {
    if (!activeStatFilter) return data;
    return data.filter((item) => {
      let matchesCategory = true;
      switch (activeStatFilter.category) {
        case 'active':
          matchesCategory = item.active !== false;
          break;
        case 'critical':
          matchesCategory = item.safetyLevel === 'CRITICAL';
          break;
        case 'total':
        default:
          matchesCategory = true;
          break;
      }
      if (activeStatFilter.scope === 'new') {
        return matchesCategory && !isEntityViewed(item.id as string);
      }
      return matchesCategory;
    });
  }, [data, activeStatFilter, isEntityViewed]);

  // ============================================================================
  // CRUD ACTIONS
  // ============================================================================

  const handleCreate = useCallback(() => {
    if (metadata.aiCreateRoute) {
      navigate(metadata.aiCreateRoute);
      return;
    }
    setSelectedItem(null);
    openCreate();
  }, [metadata.aiCreateRoute, navigate, openCreate]);

  const handleView = useCallback((item: PrimitiveItem) => {
    markViewed(item.id as string);

    // DRR-179: Task display_hint routing — "page" navigates to full-page view
    if (primitiveType === 'task') {
      const meta = item.metadata as Record<string, unknown> | undefined;
      const hint = typeof meta === 'object' && meta !== null
        ? (meta as Record<string, unknown>).display_hint
        : undefined;
      if (hint === 'page') {
        navigate(`/ferp-core/tasks/${item.id as string}`);
        return;
      }
    }

    setSelectedItem(item);
    // In master-detail mode, clicking updates inline detail — no drawer
    if (viewMode !== 'master-detail') {
      // Instances default to Flow tab (shows state machine + tasks)
      setDrawerTab(primitiveType === 'instance' ? 'flow' : 'details');
      openDrawer();
    }
  }, [openDrawer, markViewed, viewMode, primitiveType, navigate]);

  const handleEdit = useCallback((item: PrimitiveItem) => {
    setSelectedItem(item);
    setDrawerTab('edit');
    openDrawer();
  }, [openDrawer]);

  const handleArchive = useCallback(
    async (item: PrimitiveItem) => {
      if (!confirm(`Archive "${item.name}"? This will set is_active=false but preserve the record.`)) {
        return;
      }

      try {
        await archiveEntity(item.id);

        notifications.show({
          title: 'Archived',
          message: `${item.name} has been archived`,
          color: 'green',
        });

        refetch();
      } catch (err) {
        notifications.show({
          title: 'Archive Failed',
          message: err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
      }
    },
    [archiveEntity, refetch]
  );

  const handleRunWorkflow = useCallback((item: PrimitiveItem) => {
    setLaunchItem(item);
    openLaunch();
  }, [openLaunch]);


  // Stable refs for Effect 1's cellClick listener.
  // Refs are updated synchronously every render, so the listener always has the latest
  // callback without being included in Effect 1's deps — this prevents table rebuild
  // (destroy + recreate) whenever a callback identity changes.
  const handleViewRef = useRef(handleView);
  handleViewRef.current = handleView;
  const handleEditRef = useRef(handleEdit);
  handleEditRef.current = handleEdit;
  const handleArchiveRef = useRef(handleArchive);
  handleArchiveRef.current = handleArchive;
  const handleRunWorkflowRef = useRef(handleRunWorkflow);
  handleRunWorkflowRef.current = handleRunWorkflow;
  const labelRef = useRef(label);
  labelRef.current = label;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const handleFormSuccess = useCallback(() => {
    closeCreate();
    closeDrawer();
    refetch();
    notifications.show({
      title: 'Success',
      message: `${label} saved successfully`,
      color: 'green',
    });
  }, [label, closeCreate, closeDrawer, refetch]);

  const handleCreateSubmit = useCallback(
    async (data: PrimitiveMutationInput) => {
      try {
        await create(data);
        handleFormSuccess();
      } catch (err) {
        notifications.show({
          title: 'Create Failed',
          message: err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
        throw err;
      }
    },
    [create, handleFormSuccess]
  );

  const handleEditSubmit = useCallback(
    async (data: PrimitiveMutationInput) => {
      if (!selectedItem) return;

      try {
        await create(data);
        handleFormSuccess();
      } catch (err) {
        notifications.show({
          title: 'Update Failed',
          message: err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
        throw err;
      }
    },
    [selectedItem, create, handleFormSuccess]
  );

  const handleExport = useCallback(
    (format: 'csv' | 'json') => {
      const timestamp = new Date().toISOString().split('T')[0];
      tabulatorInstance.current?.download(format, `${primitiveType}s_${timestamp}.${format}`);
    },
    [primitiveType]
  );

  // ============================================================================
  // TABULATOR INITIALIZATION
  // ============================================================================

  // Effect 1: Create/recreate table when structure changes (columns, layout, handlers).
  // Data is loaded separately via replaceData to avoid destroy/recreate on every poll.
  useEffect(() => {
    if (!tableRef.current) return;

    rebuildingRef.current = true;
    try {
      if (tabulatorInstance.current) {
        tabulatorInstance.current.destroy();
        tabulatorInstance.current = null;
      }
    } catch (err) {
      console.warn('[TabulatorPrimitiveManager] Tabulator destroy failed (safe to ignore):', err);
      tabulatorInstance.current = null;
    }

    const columns = getVisibleColumns(allColumns, columnVisibility);

    // Determine sort field — only use a field that exists in visible columns
    const visibleFields = columns.map(c => c.field);
    const initialSort = visibleFields.includes('updatedAt')
      ? [{ column: 'updatedAt', dir: 'desc' as const }]
      : visibleFields.includes('createdAt')
        ? [{ column: 'createdAt', dir: 'desc' as const }]
        : visibleFields.includes('name')
          ? [{ column: 'name', dir: 'asc' as const }]
          : [];

    // Deep clone to avoid mutating Apollo's frozen cache objects
    const mutableData = structuredClone(data);

    try {
      tableBuiltRef.current = false;
      const thisGen = ++tableGenRef.current;
      tabulatorInstance.current = new Tabulator(tableRef.current, {
        ...TABULATOR_DEFAULTS,
        data: mutableData,
        columns,
        placeholder: `No ${labelRef.current.toLowerCase()}s found`,
        initialSort,
        ...(isFullscreen ? { height: 'calc(100vh - 80px)' } : {}),
        // DRR-178 Phase 4: Compound scoping — groupBy accepts array for 2-level grouping
        // When secondary grouping is active, use compound formatter that handles mixed value types
        ...(groupField ? {
          groupBy: secondaryGroupField ? [groupField, secondaryGroupField] : groupField,
          groupHeader: secondaryGroupField
            ? createCompoundGroupHeaderFormatter(resolveTaskGroupName)
            : (groupField === 'taskGroupRef'
              ? createGroupHeaderFormatter(resolveTaskGroupName)
              : groupHeaderFormatter),
        } : {}),
      });

      tabulatorInstance.current.on('tableBuilt', () => {
        // Only mark ready if this callback belongs to the current table generation.
        // Stale callbacks from destroyed instances are silently discarded.
        if (thisGen !== tableGenRef.current) return;
        tableBuiltRef.current = true;
        rebuildingRef.current = false;
        // Apply caller-provided column filters (e.g., status=ACTIVE from SubjectFilterHeader)
        if (filterBy && filterBy.length > 0 && tabulatorInstance.current) {
          filterBy.forEach((f) => {
            tabulatorInstance.current!.setHeaderFilterValue(f.field, f.value);
          });
        }
      });

      // Action button + row expansion click handlers
      tabulatorInstance.current.on('cellClick', (e: Event, cell: TabulatorCell) => {
        const field = cell.getColumn().getField();
        const item = cell.getRow().getData() as PrimitiveItem;

        // Selection mode: any click selects the row
        if (onSelectRef.current) {
          onSelectRef.current(item);
          return;
        }

        // Row expander column — toggle accordion preview
        if (field === '_expander') {
          const rowId = String(item.id ?? '');
          const rowEl = cell.getRow().getElement() as HTMLElement;
          const expanderEl = cell.getElement().querySelector('.ferp-row-expander') as HTMLElement | null;

          if (expandedRowsRef.current.has(rowId)) {
            // Collapse: remove preview + rotate arrow back
            expandedRowsRef.current.delete(rowId);
            expanderEl?.classList.remove('expanded');
            const preview = rowEl.nextElementSibling;
            if (preview?.classList.contains('ferp-row-preview')) {
              preview.remove();
            }
          } else {
            // Expand: inject preview strip after row
            expandedRowsRef.current.add(rowId);
            expanderEl?.classList.add('expanded');

            // Build preview HTML: name + status badge + description + key fields
            const name = String(item.name ?? 'Unnamed');
            const status = item.status as string | undefined;
            const desc = item.description as string | undefined;
            const statusHtml = status
              ? `<span class="ferp-tbl-badge ferp-tbl-badge--${STATUS_BADGE_MAP[status] ?? 'normal'}" style="margin-left:8px">${status}</span>`
              : '';
            const descHtml = desc
              ? `<div style="color:var(--mantine-color-dimmed);font-size:12px;margin-top:4px">${desc.length > 120 ? desc.slice(0, 120) + '...' : desc}</div>`
              : '';

            // Pick up to 3 extra key-value fields (skip system fields)
            const skipFields = new Set(['id', '__typename', 'name', 'description', 'status', 'active', 'createdAt', 'updatedAt', 'createdBy', 'version', 'safetyLevel', 'tenantId']);
            const kvFields: string[] = [];
            for (const [k, v] of Object.entries(item)) {
              if (skipFields.has(k) || k.startsWith('_') || v == null || v === '') continue;
              const displayVal = typeof v === 'object' ? JSON.stringify(v) : String(v);
              kvFields.push(`<span style="color:var(--mantine-color-dimmed);font-size:11px">${k}:</span> <span style="font-size:12px">${displayVal.length > 40 ? displayVal.slice(0, 40) + '...' : displayVal}</span>`);
              if (kvFields.length >= 3) break;
            }
            const kvHtml = kvFields.length
              ? `<div style="margin-top:4px;display:flex;gap:12px;flex-wrap:wrap">${kvFields.join('')}</div>`
              : '';

            const previewDiv = document.createElement('div');
            previewDiv.className = 'ferp-row-preview';
            previewDiv.innerHTML = `<div><strong>${name}</strong>${statusHtml}</div>${descHtml}${kvHtml}`;
            rowEl.insertAdjacentElement('afterend', previewDiv);
          }
          return;
        }

        if (field === '_actions') {
          const target = e.target as HTMLElement;
          if (target.classList.contains('tabulator-btn-view')) handleViewRef.current(item);
          if (target.classList.contains('tabulator-btn-edit')) handleEditRef.current(item);
          if (target.classList.contains('tabulator-btn-archive')) handleArchiveRef.current(item);
          if (target.classList.contains('tabulator-btn-run')) handleRunWorkflowRef.current(item);
          if (target.classList.contains('tabulator-btn-instances')) {
            navigate(`/ferp-core/instances?workflow=${encodeURIComponent(item.id as string)}`);
          }
        } else {
          // Row click (non-action column) opens detail drawer
          handleViewRef.current(item);
        }
      });

      // Row hover effects
      tabulatorInstance.current.on('rowMouseEnter', (_: unknown, row: { getElement: () => HTMLElement }) => {
        row.getElement().style.cursor = 'pointer';
      });
    } catch (err) {
      console.error('[TabulatorPrimitiveManager] Failed to initialize Tabulator:', err);
    }

    return () => {
      tableBuiltRef.current = false;
      rebuildingRef.current = false;
      try {
        tabulatorInstance.current?.destroy();
      } catch {
        // Tabulator cleanup may fail during rapid re-renders — safe to ignore
      }
      tabulatorInstance.current = null;
    };
    // NOTE: `data` is intentionally excluded — see Effect 2 below.
    // NOTE: `label`, `handleView`, `handleEdit`, `handleArchive`, `onSelect` are intentionally
    // excluded — they're accessed via refs (handleViewRef etc.) so the table is only
    // rebuilt when STRUCTURE changes (columns, fullscreen, grouping), not on callback updates.
    // `filterBy` IS included: changing the initial filters requires a rebuild so the
    // tableBuilt callback closure captures the new values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns, columnVisibility, primitiveType, isFullscreen, groupField, secondaryGroupField, filterBy]);

  // Effect 2: Update table data without destroying/recreating the table.
  // This prevents the "row this cell is attached to cannot be found" Tabulator errors
  // that occur when the table is rebuilt during Apollo polling or refetch cycles.
  useEffect(() => {
    if (!tabulatorInstance.current || !tableBuiltRef.current || !data) return;
    // Guard: skip replaceData while Effect 1 is rebuilding the table instance.
    if (rebuildingRef.current) return;
    // Guard: element must still be attached to the DOM — navigating away can
    // orphan the Tabulator instance before React runs cleanup.
    if (!tableRef.current || !document.contains(tableRef.current)) return;
    try {
      // Clear expanded rows — DOM preview elements are destroyed on replaceData
      expandedRowsRef.current.clear();
      // replaceData returns a Promise — catch async errors (e.g. DOM detached
      // mid-load after navigation) to prevent "Uncaught (in promise)" crashes.
      void tabulatorInstance.current.replaceData(structuredClone(data)).catch(() => {});
    } catch {
      // Synchronous errors — safe to ignore
    }
  }, [data]);

  // Load initial data — skip when overrideData is provided (parent manages its own fetching)
  // refetch ref excluded from deps: Apollo returns new refs on cache updates, causing infinite loops
  const hasOverrideData = !!overrideData;
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (!skip && !hasOverrideData && !initialFetchDone.current) {
      initialFetchDone.current = true;
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, hasOverrideData]);

  // ============================================================================
  // ERROR STATES
  // ============================================================================

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} c="yellow" title="No Tenant Selected">
          Please select a tenant to view {label.toLowerCase()}s.
        </Alert>
      </Container>
    );
  }

  // Mutations not available (read-only mode) — create is null for some primitives
  const isReadOnly = !create;

  // Error with no data — classify the error properly
  if (error && !data.length) {
    const errorType = classifyError(error);
    const display = ERROR_DISPLAY[errorType];
    const ErrorIcon = display.icon;

    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" c={metadata.phaseColor}>
              <PrimitiveIcon size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>{label}s</Title>
              <Text c="dimmed" size="sm">
                {metadata.description}
              </Text>
            </div>
          </Group>
          <Alert
            icon={<ErrorIcon size={16} />}
            c={display.color}
            title={display.title}
          >
            <Stack gap="sm">
              <Text size="sm">
                {ERROR_MESSAGES[errorType] || error.message}
              </Text>
              {errorType === 'validation' && (
                <Text size="xs" c="dimmed">
                  Detail: {error.message}
                </Text>
              )}
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                size="sm"
                onClick={() => refetch()}
                style={{ alignSelf: 'flex-start' }}
              >
                Retry
              </Button>
            </Stack>
          </Alert>
        </Stack>
      </Container>
    );
  }

  // ============================================================================
  // MAIN RENDER (Error Monitor quality)
  // ============================================================================

  return (
    <Container fluid py="xl" px="md">
      <Stack gap="lg">
        {/* Header — hidden in compact/selection mode */}
        {!compact && (
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="lg" variant="light" c={metadata.phaseColor}>
                <PrimitiveIcon size={20} />
              </ThemeIcon>
              <div>
                <Title order={2}>{label}s</Title>
                <Text c="dimmed" size="sm">
                  {metadata.description}
                </Text>
              </div>
            </Group>

            <Group gap="sm">
              {primitiveType === 'workflow' && selectedItem?.active && (
                <Button
                  leftSection={<IconRocket size={16} />}
                  color="green"
                  onClick={() => handleRunWorkflow(selectedItem)}
                  title={`Launch: ${selectedItem.name ?? selectedItem.id}`}
                >
                  Run: {String(selectedItem.name ?? '').slice(0, 22) || 'Selected'}
                </Button>
              )}
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleCreate}
                disabled={isReadOnly}
                title={isReadOnly ? 'Create not available - repository is read-only' : undefined}
              >
                Create
              </Button>
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                onClick={() => refetch()}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                leftSection={<IconHelp size={16} />}
                variant="subtle"
                onClick={openDocs}
                c="gray"
              >
                Help
              </Button>
            </Group>
          </Group>
        )}

        {/* Selection mode banner */}
        {onSelect && (
          <Alert variant="light" color="blue" title="Selection Mode">
            Click any row to load it into the Designer
          </Alert>
        )}

        {/* Optional header slot (e.g., My Tasks / All Tasks toggle) */}
        {headerSlot}

        {/* Partial error banner */}
        {error && data.length > 0 && (
          <Alert icon={<IconAlertTriangle size={16} />} c="yellow" title="Partial Data">
            Some {label.toLowerCase()} data may be incomplete. {error.message}
          </Alert>
        )}

        {/* Summary Stats + Lifecycle Phase — hidden in compact/selection mode */}
        {!compact && (
          <>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              <InteractiveStatCard
                label="Total"
                total={data.length}
                newCount={newTotalCount}
                color={metadata.phaseColor}
                icon={<Badge size="sm" c={metadata.phaseColor} variant="light">{primitiveType}</Badge>}
                isActive={activeStatFilter?.category === 'total'}
                activeScope={activeStatFilter?.category === 'total' ? activeStatFilter.scope : null}
                onClickAll={() => setActiveStatFilter(activeStatFilter?.category === 'total' && activeStatFilter.scope === 'all' ? null : { category: 'total', scope: 'all' })}
                onClickNew={() => setActiveStatFilter(activeStatFilter?.category === 'total' && activeStatFilter.scope === 'new' ? null : { category: 'total', scope: 'new' })}
              />
              <InteractiveStatCard
                label="Active"
                total={activeCount}
                newCount={newActiveCount}
                color="green"
                isActive={activeStatFilter?.category === 'active'}
                activeScope={activeStatFilter?.category === 'active' ? activeStatFilter.scope : null}
                onClickAll={() => setActiveStatFilter(activeStatFilter?.category === 'active' && activeStatFilter.scope === 'all' ? null : { category: 'active', scope: 'all' })}
                onClickNew={() => setActiveStatFilter(activeStatFilter?.category === 'active' && activeStatFilter.scope === 'new' ? null : { category: 'active', scope: 'new' })}
              />
              <InteractiveStatCard
                label="Critical"
                total={criticalCount}
                newCount={newCriticalCount}
                color="red"
                isActive={activeStatFilter?.category === 'critical'}
                activeScope={activeStatFilter?.category === 'critical' ? activeStatFilter.scope : null}
                onClickAll={() => setActiveStatFilter(activeStatFilter?.category === 'critical' && activeStatFilter.scope === 'all' ? null : { category: 'critical', scope: 'all' })}
                onClickNew={() => setActiveStatFilter(activeStatFilter?.category === 'critical' && activeStatFilter.scope === 'new' ? null : { category: 'critical', scope: 'new' })}
              />
              <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                  Phase
                </Text>
                <Badge size="lg" c={phaseConfig.color} variant="light">
                  {phaseConfig.label}
                </Badge>
              </Paper>
            </SimpleGrid>

            {/* Lifecycle Phase Indicator */}
            <Paper withBorder p="sm" radius="md">
              <Group gap="xs" justify="center" wrap="nowrap">
                {LIFECYCLE_PHASES.map((phase, idx) => {
                  const phaseMeta = PHASE_CONFIG[phase];
                  const isCurrentPhase = metadata.phase === phase;
                  const primitivesInPhase = getPrimitivesByPhase(phase);
                  return (
                    <Group key={phase} gap="xs" wrap="nowrap">
                      {idx > 0 && <IconArrowRight size={14} color="var(--mantine-color-dimmed)" />}
                      <Tooltip label={primitivesInPhase.map(p => p.name).join(', ')} position="bottom" multiline>
                        <Badge
                          size={isCurrentPhase ? 'lg' : 'md'}
                          variant={isCurrentPhase ? 'filled' : 'light'}
                          c={phaseMeta.color}
                          style={isCurrentPhase ? { boxShadow: `0 0 0 2px var(--mantine-color-${phaseMeta.color}-3)` } : undefined}
                        >
                          {phaseMeta.label} ({primitivesInPhase.length})
                        </Badge>
                      </Tooltip>
                    </Group>
                  );
                })}
              </Group>
            </Paper>
          </>
        )}

        {/* Tabulator Table (Card wrapper like Error Monitor) */}
        <Card
          ref={cardRef}
          withBorder
          p="lg"
          radius={isFullscreen ? 0 : 'md'}
          style={isFullscreen ? {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            overflow: 'auto',
            background: 'var(--mantine-color-body)',
          } : undefined}
        >
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Title order={4}>
                {label}s ({data.length})
              </Title>
              {isFullscreen && (
                <Badge size="sm" variant="light" color="blue">Fullscreen - Press Esc to exit</Badge>
              )}
              {activeStatFilter && (
                <Tooltip label="Clear filter">
                  <Badge
                    size="lg"
                    variant="light"
                    color={activeStatFilter.scope === 'new' ? 'orange' : 'blue'}
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        color={activeStatFilter.scope === 'new' ? 'orange' : 'blue'}
                        onClick={() => setActiveStatFilter(null)}
                      >
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    {activeStatFilter.category} ({activeStatFilter.scope})
                  </Badge>
                </Tooltip>
              )}
            </Group>
            <Group gap="xs">
              {/* View Mode Toggle: Table / Card / Master-Detail */}
              <ViewModeToggle
                value={viewMode}
                onChange={setViewMode}
                enabledModes={scopeDimensions.length > 0
                  ? ['table', 'card', 'master-detail', 'kanban', 'spatial', 'timeline']
                  : ['table', 'card', 'master-detail']
                }
              />

              {/* DRR-178: Scope Dimension Picker (replaces hardcoded Group By menu) */}
              {viewMode !== 'card' && (
                <Menu shadow="md" width={220} zIndex={1100}>
                  <Menu.Target>
                    <Tooltip label="Scope / Group by dimension">
                      <ActionIcon
                        variant={groupField ? 'filled' : 'light'}
                        size="lg"
                        color={groupField ? 'blue' : undefined}
                        data-testid="scope-dimension-picker"
                      >
                        <IconLayoutRows size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Scope By</Menu.Label>
                    <Menu.Item
                      onClick={() => { setGroupField(null); onGroupByChange?.(null); }}
                      rightSection={!groupField ? <IconCheck size={14} /> : undefined}
                    >
                      None (flat list)
                    </Menu.Item>
                    <Menu.Divider />
                    {scopeDimensions.length > 0 ? (
                      // DRR-178: Backend-driven dimensions from Setting config
                      // Selecting a dimension updates BOTH groupField AND viewMode
                      scopeDimensions.map((dim) => {
                        const rendererMode = dim.suggestedRenderer as DisplayViewMode;
                        const isValidRenderer = ['table', 'card', 'master-detail', 'kanban', 'spatial', 'timeline'].includes(rendererMode);
                        return (
                          <Menu.Item
                            key={dim.dimensionId}
                            data-testid={`scope-dimension-${dim.dimensionId}`}
                            onClick={() => {
                              setGroupField(dim.field);
                              onGroupByChange?.(dim.field);
                              // Switch renderer to suggested mode (DRR-178 D2: suggestion, not enforcement)
                              if (isValidRenderer) {
                                setViewMode(rendererMode);
                              }
                            }}
                            rightSection={groupField === dim.field ? <IconCheck size={14} /> : undefined}
                          >
                            <Group gap={8}>
                              <Text size="sm">{dim.label}</Text>
                              <Badge size="xs" variant="light" color="gray">{dim.suggestedRenderer}</Badge>
                            </Group>
                          </Menu.Item>
                        );
                      })
                    ) : (
                      // Fallback: merge system fields + schema fields (pre-DRR-178 behavior)
                      (() => {
                        const systemFieldNames = new Set(fallbackFields.map((f) => f.field));
                        const schemaOnly = schemaGroupableFields.filter((f) => !systemFieldNames.has(f.field));
                        const merged = [
                          ...fallbackFields,
                          ...schemaOnly.map((f) => ({ field: f.field, label: f.title })),
                        ];
                        return merged.map((f) => (
                          <Menu.Item
                            key={f.field}
                            onClick={() => { setGroupField(f.field); onGroupByChange?.(f.field); }}
                            rightSection={groupField === f.field ? <IconCheck size={14} /> : undefined}
                          >
                            {f.label}
                          </Menu.Item>
                        ));
                      })()
                    )}
                    {/* DRR-178 Phase 4: Compound scoping — "Then by..." secondary dimension */}
                    {groupField && scopeDimensions.length > 1 && (
                      <>
                        <Menu.Divider />
                        <Menu.Label>Then by</Menu.Label>
                        <Menu.Item
                          onClick={() => setSecondaryGroupField(null)}
                          rightSection={!secondaryGroupField ? <IconCheck size={14} /> : undefined}
                        >
                          None
                        </Menu.Item>
                        {scopeDimensions
                          .filter((dim) => dim.field !== groupField)
                          .map((dim) => (
                            <Menu.Item
                              key={`secondary-${dim.dimensionId}`}
                              onClick={() => setSecondaryGroupField(dim.field)}
                              rightSection={secondaryGroupField === dim.field ? <IconCheck size={14} /> : undefined}
                            >
                              <Text size="xs">{dim.label}</Text>
                            </Menu.Item>
                          ))}
                      </>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}

              {/* Fullscreen Toggle */}
              <Tooltip label={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand to fullscreen'}>
                <ActionIcon variant="light" size="lg" onClick={toggleFullscreen}>
                  {isFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                </ActionIcon>
              </Tooltip>
              {/* Column Visibility Toggle (grouped by FormSchema section) */}
              <Popover
                shadow="md"
                withinPortal
                zIndex={1100}
                position="bottom-end"
                onClose={() => setColumnSearch('')}
              >
                <Popover.Target>
                  <Tooltip label="Show/hide columns">
                    <ActionIcon variant="light" size="lg" title="Columns">
                      <IconColumns size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Popover.Target>
                <Popover.Dropdown
                  p={0}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'calc(85vh - 80px)',
                    width: 'auto',
                  }}
                >
                  {/* Search input — sticky top */}
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <TextInput
                      placeholder="Search columns..."
                      size="xs"
                      leftSection={<IconSearch size={14} />}
                      value={columnSearch}
                      onChange={(e) => setColumnSearch(e.currentTarget.value)}
                    />
                  </div>
                  {/* Scrollable column groups — multi-column when many groups */}
                  <ScrollArea style={{ flex: 1 }} offsetScrollbars>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.min(columnGroups.length, 5)}, minmax(190px, 1fr))`,
                        gap: '4px 12px',
                        padding: '4px 8px',
                        maxWidth: 'min(92vw, 1100px)',
                      }}
                    >
                      {columnGroups.length === 0 && (
                        <Text size="sm" c="dimmed" ta="center" py="sm" style={{ gridColumn: '1 / -1' }}>
                          No columns match
                        </Text>
                      )}
                      {columnGroups.map((group) => {
                        const groupAllVisible = group.columns.every(col => isColumnVisible(col.field));
                        const groupSomeVisible = group.columns.some(col => isColumnVisible(col.field));

                        return (
                          <div
                            key={group.category}
                            style={{
                              paddingBottom: 4,
                            }}
                          >
                            {/* Section header with toggle-all */}
                            <UnstyledButton
                              w="100%"
                              px={4}
                              py={4}
                              onClick={() => toggleGroupVisibility(group.columns)}
                              style={{
                                borderBottom: '1px solid var(--mantine-color-default-border)',
                                marginBottom: 2,
                              }}
                            >
                              <Group justify="space-between" wrap="nowrap">
                                <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                                  {group.category}
                                </Text>
                                <Checkbox
                                  size="xs"
                                  checked={groupAllVisible}
                                  indeterminate={groupSomeVisible && !groupAllVisible}
                                  onChange={() => toggleGroupVisibility(group.columns)}
                                  styles={{ input: { cursor: 'pointer' } }}
                                />
                              </Group>
                            </UnstyledButton>
                            {/* Individual column items */}
                            {group.columns.map(col => (
                              <UnstyledButton
                                key={col.field}
                                w="100%"
                                px={4}
                                py={3}
                                onClick={() => toggleColumn(col.field)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  borderRadius: 'var(--mantine-radius-sm)',
                                }}
                                className="ferp-column-item"
                              >
                                {isColumnVisible(col.field) ? (
                                  <IconCheck size={14} color="var(--mantine-color-green-6)" />
                                ) : (
                                  <span style={{ width: 14 }} />
                                )}
                                <Text size="sm" c={isColumnVisible(col.field) ? undefined : 'dimmed'}>
                                  {col.title}
                                </Text>
                              </UnstyledButton>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                  {/* Footer — sticky bottom */}
                  <Group px="sm" py={6} gap="xs" justify="center" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                    <UnstyledButton px="sm" py={2} onClick={selectAllColumns}>
                      <Text size="xs" c="dimmed">All</Text>
                    </UnstyledButton>
                    <Divider orientation="vertical" />
                    <UnstyledButton px="sm" py={2} onClick={deselectAllColumns}>
                      <Text size="xs" c="dimmed">None</Text>
                    </UnstyledButton>
                    <Divider orientation="vertical" />
                    <UnstyledButton px="sm" py={2} onClick={resetColumns}>
                      <Text size="xs" c="dimmed">Reset</Text>
                    </UnstyledButton>
                  </Group>
                </Popover.Dropdown>
              </Popover>

              {/* Lens Preset Selector — only when FormSchema + lenses available */}
              {hasSchema && activeLenses.length > 0 && (
                <LensPresetSelector
                  activeLenses={activeLenses}
                  selectedLensId={selectedLensId}
                  onSelect={(lensId) => {
                    setSelectedLensId(lensId);
                    if (lensId && tenantId) {
                      evaluateLens({ actorId: tenantId });
                    }
                  }}
                  loading={isEvaluating}
                />
              )}

              {/* Export Menu */}
              <Menu shadow="md" width={200} zIndex={1100}>
                <Menu.Target>
                  <ActionIcon variant="light" size="lg" title="Export Data">
                    <IconDownload size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Export Format</Menu.Label>
                  <Menu.Item onClick={() => handleExport('csv')}>
                    Download CSV
                  </Menu.Item>
                  <Menu.Item onClick={() => handleExport('json')}>
                    Download JSON
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>

          {viewMode === 'card' ? (
            <>
              {loading && !data.length && (
                <Center py="xl">
                  <Stack ta="center" gap="md">
                    <Loader size="lg" />
                    <Text c="dimmed">Loading {label.toLowerCase()}s...</Text>
                  </Stack>
                </Center>
              )}
              {(!loading || data.length > 0) && (
                <EntityCardGrid
                  items={filteredData}
                  fieldAdapter={cardFieldAdapter}
                  onItemClick={onSelect ?? handleView}
                  selectedItemId={selectedItem?.id as string | undefined}
                  getId={(item) => item.id as string}
                  emptyMessage={`No ${label.toLowerCase()}s found`}
                  maxHeight={isFullscreen ? 'calc(100vh - 80px)' : '600px'}
                  renderActionSlot={renderCardAction}
                />
              )}
            </>
          ) : viewMode === 'master-detail' ? (
            <MasterDetailLayout
              storageKey={primitiveType}
              listContent={
                <div style={{ position: 'relative' }}>
                  {loading && !data.length && (
                    <Center style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'var(--mantine-color-body)' }}>
                      <Stack ta="center" gap="md">
                        <Loader size="lg" />
                        <Text c="dimmed">Loading {label.toLowerCase()}s...</Text>
                      </Stack>
                    </Center>
                  )}
                  <div ref={tableRef} />
                </div>
              }
              detailContent={
                selectedItem ? (
                  <EntityInlineDetail
                    entity={selectedItem}
                    primitiveType={primitiveType}
                    onClose={() => setSelectedItem(null)}
                    onExpand={() => {
                      openDrawer();
                      setDrawerTab('details');
                    }}
                  />
                ) : null
              }
              emptyDetailMessage={`Click a ${label.toLowerCase()} to view details`}
            />
          ) : viewMode === 'kanban' ? (
            /* DRR-178: Kanban board view — groups items by columnField into columns */
            <>
              {loading && !data.length ? (
                <Center py="xl">
                  <Stack ta="center" gap="md">
                    <Loader size="lg" />
                    <Text c="dimmed">Loading {label.toLowerCase()}s...</Text>
                  </Stack>
                </Center>
              ) : (
                <KanbanRenderer
                  items={filteredData}
                  columnField={groupField ?? 'status'}
                  fieldAdapter={(item: PrimitiveItem) => ({
                    name: (item.name as string) ?? 'Unnamed',
                    subtitle: (item.description as string) ?? undefined,
                    status: item.status as string,
                    safetyLevel: item.safetyLevel as string,
                  })}
                  onItemClick={onSelect ?? handleView}
                  getId={(item) => item.id as string}
                  maxHeight={isFullscreen ? 'calc(100vh - 80px)' : '600px'}
                />
              )}
            </>
          ) : viewMode === 'spatial' ? (
            /* DRR-178 Phase 5: Spatial grid view — groups items by location field */
            <>
              {loading && !data.length ? (
                <Center py="xl">
                  <Stack ta="center" gap="md">
                    <Loader size="lg" />
                    <Text c="dimmed">Loading {label.toLowerCase()}s...</Text>
                  </Stack>
                </Center>
              ) : (
                <SpatialRenderer
                  items={filteredData}
                  locationField={groupField ?? 'audit_info.location'}
                  fieldAdapter={(item: PrimitiveItem) => ({
                    name: (item.name as string) ?? 'Unnamed',
                    subtitle: (item.description as string) ?? undefined,
                    safetyLevel: item.safetyLevel as string,
                  })}
                  onItemClick={onSelect ?? handleView}
                  getId={(item) => item.id as string}
                  maxHeight={isFullscreen ? 'calc(100vh - 80px)' : '600px'}
                  coordinates={hasSpatialCoords ? spatialNodes.map((n) => ({
                    location: n.name,
                    x: n.x,
                    y: n.y,
                  })) : undefined}
                  floors={spatialFloors.length > 1 ? spatialFloors.map((f) => ({
                    floor: f,
                    label: `Floor ${f}`,
                  })) : undefined}
                />
              )}
            </>
          ) : viewMode === 'timeline' ? (
            /* DRR-178: Timeline view — groups items by time blocks */
            <TimelineRenderer
              items={filteredData}
              fieldAdapter={(item: PrimitiveItem) => ({
                id: (item.id as string) ?? '',
                name: (item.name as string) ?? 'Unnamed',
                scheduledStart: item.scheduledStart as string | undefined,
                dueAt: item.dueAt as string | undefined,
                status: item.status as string | undefined,
                priority: item.priority as string | undefined,
                safetyLevel: item.safetyLevel as string | undefined,
                taskGroupRef: item.taskGroupRef as string | undefined,
                subjectActorId: item.subjectActorId as string | undefined,
              })}
              onItemClick={onSelect ?? handleView}
              maxHeight={isFullscreen ? 'calc(100vh - 80px)' : '600px'}
            />
          ) : (
            <div style={{ position: 'relative' }}>
              {loading && !data.length && (
                <Center style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'var(--mantine-color-body)' }}>
                  <Stack ta="center" gap="md">
                    <Loader size="lg" />
                    <Text c="dimmed">Loading {label.toLowerCase()}s...</Text>
                  </Stack>
                </Center>
              )}
              <div ref={tableRef} />
            </div>
          )}
        </Card>
      </Stack>

      {/* Create Modal (stays as modal for new entity creation) */}
      <PrimitiveFormModal
        opened={createOpened}
        onClose={closeCreate}
        primitiveType={primitiveType}
        mode="create"
        initialData={null}
        onSubmit={handleCreateSubmit}
        loading={mutationLoading}
      />

      {/* Detail Panel - opens on row click */}
      <EntityDetailPanel
        entity={selectedItem}
        primitiveType={primitiveType}
        opened={drawerOpened}
        onClose={closeDrawer}
        isReadOnly={isReadOnly}
        onEntityUpdated={refetch}
        navigation={rowNavigation}
        breadcrumbs={breadcrumbs}
        allEntities={data ?? undefined}
        extraQuickActions={
          primitiveType === 'workflow' && selectedItem ? (
            <>
              {selectedItem.active && (
                <Button
                  size="xs"
                  color="green"
                  leftSection={<IconRocket size={14} />}
                  onClick={() => { closeDrawer(); handleRunWorkflow(selectedItem); }}
                >
                  Run
                </Button>
              )}
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconTopologyFull size={14} />}
                onClick={() => {
                  closeDrawer();
                  const name = encodeURIComponent(String(selectedItem.name ?? selectedItem.id));
                  navigate(`/ferp-core/workflows/studio?tab=inspector&workflowId=${selectedItem.id}&workflowName=${name}`);
                }}
              >
                Open in Designer
              </Button>
            </>
          ) : undefined
        }
        editContent={
          selectedItem && !isReadOnly ? (
            <PrimitiveFormModal
              opened={true}
              onClose={() => setDrawerTab('details')}
              primitiveType={primitiveType}
              mode="edit"
              initialData={selectedItem}
              onSubmit={handleEditSubmit}
              loading={mutationLoading}
              inline
            />
          ) : undefined
        }
      />

      {/* Documentation Modal */}
      <PrimitiveDocumentationModal
        opened={docsOpened}
        onClose={closeDocs}
        primitiveType={primitiveType}
      />

      {/* Workflow Launch Modal */}
      {primitiveType === 'workflow' && tenantId && (
        <WorkflowLaunchModal
          opened={launchOpened}
          onClose={closeLaunch}
          workflowItem={launchItem}
          tenantId={tenantId}
        />
      )}
    </Container>
  );
}

export default TabulatorPrimitiveManager;
